import { Wheel } from "./Wheel";

export class WheelPointer extends Phaser.GameObjects.Container {
  private wheel: Wheel;
  private pointer: Phaser.GameObjects.Image;

  private theta = 0; // current deflection (rad)
  private omega = 0; // angular velocity (rad/s)

  private k: number = 90; // spring constant (N/m)
  private c: number = 18; // damping coefficient (N·s/m)
  private maxKick: number = 1.2; // max kick (rad/s)
  private mountOffset: number = 8; // offset from wheel center to pointer mount
  private baseRotation: number = 0; // base rotation offset (rad)

  private lastHitAt = -Infinity;
  private hitCooldownMs = 12;

  constructor(scene: Phaser.Scene, wheel: Wheel) {
    super(scene);

    this.wheel = wheel;

    this.pointer = scene.add.image(0, 0, "wheelPointer");
    this.pointer.setOrigin(0.5, 0); // point downwards
    this.add(this.pointer);
    scene.add.existing(this);
    this.depth = 1000; // ensure pointer is always on top

    scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);

    // flick when the wheel crosses a center
    this.wheel.on("peg-cross", this.onPegCross, this);

    this.draw();
  }

  public draw(): void {
    this.pointer.setPosition(
      this.wheel.x,
      this.wheel.y - this.wheel.radius - this.mountOffset,
    );
  }

  override update(_time: number, delta: number): void {
    const dt = delta / 1000;

    // damped spring to theta=0
    const acc = -this.k * this.theta - this.c * this.omega;
    this.omega += acc * dt;
    this.theta += this.omega * dt;

    // tiny deadzone
    if (Math.abs(this.theta) < 0.0006 && Math.abs(this.omega) < 0.0006) {
      this.theta = 0;
      this.omega = 0;
    }

    this.pointer.setRotation(this.baseRotation + this.theta);
  }

  override destroy(fromScene?: boolean): void {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.wheel.off("peg-cross", this.onPegCross, this);
    super.destroy(fromScene);
  }

  private impulseFromSpeed(speedRad: number): number {
    // Normalize speed into [0..1] across a reasonable range
    const sHigh = 25; // rad/s (your max is ~30)
    const t = Phaser.Math.Clamp(speedRad / sHigh, 0, 1);
    // Linearly fade from 1.0 rad/s at low speed to 0.12 at high speed
    const imp = Phaser.Math.Linear(1.0, 0.12, t);
    return Phaser.Math.Clamp(imp, 0.06, this.maxKick);
  }

  /** Called whenever the wheel crosses a section center. */
  private onPegCross(payload: {
    centerDeg: number;
    speed: number;
    dir: number;
  }) {
    // simple debounce (extra safety — Wheel also limits to 1 per frame below)
    const now = this.scene.time.now;
    if (now - this.lastHitAt < this.hitCooldownMs) return;
    this.lastHitAt = now;

    const s = Math.abs(payload.speed);
    const impulse = this.impulseFromSpeed(s);

    // Your Wheel emits dir = -sign(omega), so we ADD here.
    this.omega += impulse * (payload.dir || 1);
  }
}
